import { UserProperty } from '../../model/base/userProperty';
import { Variable } from '../../model/base/variable';
import { OwnedSet } from '../../model/base/ownedset';
import { stringIsNullOrEmpty } from './string.util';
import { getLogicAppParametersFromExpression } from './expression2.util';

export class UserPropertiesUtils {
    public static readonly expressionSyntaxError = 'USER_PROP_EXP_ERROR'; // Not surfaced to user.

    public static nullKeyExists(properties: OwnedSet<UserProperty>): boolean {
        if (!properties) {
            return false;
        }
        const nullKey = properties.find((property: UserProperty) => {
            return stringIsNullOrEmpty(property.name);
        });
        return Boolean(nullKey);
    }

    public static nullValueExists(properties: OwnedSet<UserProperty>): boolean {
        if (!properties) {
            return false;
        }
        const nullKey = properties.find((property: UserProperty) => {
            return stringIsNullOrEmpty(property.value);
        });
        return Boolean(nullKey);
    }

    public static getNoneUniqueNames(properties: OwnedSet<UserProperty>): string[] {
        if (!properties) {
            return [];
        }
        const noneUniqueNames: string[] = [];
        const propertyFrequency = new Map<string, number>();
        properties.forEach((property: UserProperty) => {
            const propertyName = property.name ? property.name.toLowerCase() : undefined;
            if (propertyName) {
                const newFrequency = 1 + (propertyFrequency.get(propertyName) || 0);
                propertyFrequency.set(propertyName, newFrequency);
                if (newFrequency === 2) {
                    noneUniqueNames.push(propertyName);
                }
            }
        });
        return noneUniqueNames;
    }

    /**
     * This returns a Map where the key is the userProperty name and the value is a Set of variables that are invalid.
     * The value is a Set because each property may contain the same variable multiple times as part of the expression.
     * @param userProperties User properties, these are defined by the user using free text for logging purposes.
     * @param variables The pipeline variables.
     * @returns A map containing invalid variables corresponding to each user property.
     */
    public static getErrorUserPropertyVariablesMap(userProperties: OwnedSet<UserProperty>, variables: OwnedSet<Variable>): Map<string, Set<string>> {
        const variableSet: Set<string> = new Set();
        const errorUserPropertyVariableMap = new Map<string, Set<string>>();
        variables?.forEach((variable: Variable) => {
            variableSet.add(variable.name);
        });
        userProperties?.forEach((userProperty: UserProperty) => {
            if (!userProperty.value.includes('variables')) {
                return;
            }
            const userPropertyName = userProperty.name;
            let logicAppParameters;
            try {
                logicAppParameters = getLogicAppParametersFromExpression(userProperty.value);
            } catch (error) { // An error occured when parsing for the parameters.
                // getLogicAppParametersFromExpression has bug, ex. @item()['something'] cannot be parsed - even though it is valid.
                // As this was for a repair item involving variable - we will not change further to not break customers pipelines.
                return;
                // TODO: Uncomment the below if we ever fix parsing issue above. Unlikely - user property doesn't exist in Fabric.
                // Check for this special static string (activity.ts) to detect error
                // if (!errorUserPropertyVariableMap.get(userPropertyName)) {
                //     errorUserPropertyVariableMap.set(userPropertyName, new Set<string>([UserPropertiesUtils.expressionSyntaxError]));
                // }
            }
            if (logicAppParameters) {
                logicAppParameters.forEach((logicAppParam: string) => {
                    const regex = /@variables\('([^']+)'\)/; // capture group for inside the parenthesis, excluding the single quotes
                    const match = logicAppParam.match(regex);
                    if (match) {
                        const userPropertyVariableName = match[1];  // match[0] is the whole regex match, match[1] is the first (and only) capture group
                        if (!variableSet.has(userPropertyVariableName)) {
                            if (!errorUserPropertyVariableMap.get(userPropertyName)) {
                                errorUserPropertyVariableMap.set(userPropertyName, new Set<string>());
                            }
                            errorUserPropertyVariableMap.get(userPropertyName).add(userPropertyVariableName);
                        }
                    }
                });
            }
        });
        return errorUserPropertyVariableMap;
    }
}
